import React, { useState, useRef } from 'react';
import { t } from '../i18n';

// ── CALCULATOR ──────────────────────────────────────────────
function Calculator() {
  const [display, setDisplay] = useState('0');
  const [prev, setPrev] = useState(null);
  const [op, setOp] = useState(null);
  const [reset, setReset] = useState(false);

  const press = (val) => {
    if (reset) { setDisplay(String(val)); setReset(false); return; }
    setDisplay(display === '0' ? String(val) : display + val);
  };
  const pressOp = (o) => {
    setPrev(parseFloat(display)); setOp(o); setReset(true);
  };
  const calculate = () => {
    if (prev === null || !op) return;
    const cur = parseFloat(display);
    let result = 0;
    if (op === '+') result = prev + cur;
    if (op === '-') result = prev - cur;
    if (op === '×') result = prev * cur;
    if (op === '÷') result = cur !== 0 ? prev / cur : 'Error';
    setDisplay(String(result)); setPrev(null); setOp(null); setReset(true);
  };
  const clear = () => { setDisplay('0'); setPrev(null); setOp(null); setReset(false); };

  const btns = ['C','±','%','÷','7','8','9','×','4','5','6','-','1','2','3','+','0','.','='];
  return (
    <div style={toolStyles.calcWrap}>
      <div style={toolStyles.calcDisplay}>{display}</div>
      <div style={toolStyles.calcGrid}>
        {btns.map((b, i) => (
          <button key={i}
            style={{ ...toolStyles.calcBtn,
              gridColumn: b === '0' ? 'span 2' : 'span 1',
              backgroundColor: ['÷','×','-','+','='].includes(b) ? '#6200ea' : b === 'C' ? '#ff4444' : '#f3f3f3',
              color: ['÷','×','-','+','=','C'].includes(b) ? 'white' : '#333',
            }}
            onClick={() => {
              if (b === 'C') clear();
              else if (b === '=') calculate();
              else if (['÷','×','-','+'].includes(b)) pressOp(b);
              else if (b === '±') setDisplay(String(parseFloat(display) * -1));
              else if (b === '%') setDisplay(String(parseFloat(display) / 100));
              else press(b);
            }}
          >{b}</button>
        ))}
      </div>
    </div>
  );
}

// ── UNIT CONVERTER ───────────────────────────────────────────
function UnitConverter() {
  const [category, setCategory] = useState('length');
  const [from, setFrom] = useState('');
  const [fromUnit, setFromUnit] = useState('km');
  const [toUnit, setToUnit] = useState('miles');
  const [result, setResult] = useState('');

  const units = {
    length: { km: 1000, miles: 1609.34, meter: 1, feet: 0.3048, inch: 0.0254, cm: 0.01 },
    weight: { kg: 1, pound: 0.453592, gram: 0.001, ounce: 0.0283495, ton: 1000 },
    temperature: {},
  };

  const convert = () => {
    const val = parseFloat(from);
    if (isNaN(val)) { setResult('Invalid'); return; }
    if (category === 'temperature') {
      let r;
      if (fromUnit === 'C' && toUnit === 'F') r = (val * 9/5) + 32;
      else if (fromUnit === 'F' && toUnit === 'C') r = (val - 32) * 5/9;
      else if (fromUnit === 'C' && toUnit === 'K') r = val + 273.15;
      else if (fromUnit === 'K' && toUnit === 'C') r = val - 273.15;
      else r = val;
      setResult(r.toFixed(4));
    } else {
      const base = val * units[category][fromUnit];
      setResult((base / units[category][toUnit]).toFixed(4));
    }
  };

  const catUnits = category === 'temperature' ? ['C','F','K'] : Object.keys(units[category]);

  return (
    <div style={toolStyles.converterWrap}>
      <div style={toolStyles.row}>
        {['length','weight','temperature'].map(c => (
          <button key={c} style={{ ...toolStyles.catBtn, backgroundColor: category === c ? '#6200ea' : '#f3e5f5', color: category === c ? 'white' : '#6200ea' }}
            onClick={() => { setCategory(c); setFrom(''); setResult(''); setFromUnit(c === 'temperature' ? 'C' : Object.keys(units[c] || {})[0]); setToUnit(c === 'temperature' ? 'F' : Object.keys(units[c] || {})[1]); }}
          >{c.charAt(0).toUpperCase() + c.slice(1)}</button>
        ))}
      </div>
      <input style={toolStyles.input} type="number" placeholder={t('tl_enter_value')} value={from} onChange={e => setFrom(e.target.value)} />
      <div style={toolStyles.row}>
        <select style={toolStyles.select} value={fromUnit} onChange={e => setFromUnit(e.target.value)}>
          {catUnits.map(u => <option key={u}>{u}</option>)}
        </select>
        <span style={{ fontSize: '20px' }}>→</span>
        <select style={toolStyles.select} value={toUnit} onChange={e => setToUnit(e.target.value)}>
          {catUnits.map(u => <option key={u}>{u}</option>)}
        </select>
      </div>
      <button style={toolStyles.convertBtn} onClick={convert}>{t('tl_convert')}</button>
      {result && <div style={toolStyles.result}>{from} {fromUnit} = <strong>{result} {toUnit}</strong></div>}
    </div>
  );
}

// ── WORD COUNTER ─────────────────────────────────────────────
function WordCounter() {
  const [text, setText] = useState('');
  const words = text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
  const chars = text.length;
  const charsNoSpace = text.replace(/\s/g, '').length;
  const sentences = text.split(/[.!?]+/).filter(s => s.trim()).length;
  const readTime = Math.ceil(words / 200);

  return (
    <div style={toolStyles.wcWrap}>
      <textarea style={toolStyles.wcTextarea} placeholder={t('tl_paste')} value={text} onChange={e => setText(e.target.value)} rows={8} />
      <div style={toolStyles.wcStats}>
        <div style={toolStyles.wcStat}><div style={toolStyles.wcNum}>{words}</div><div style={toolStyles.wcLabel}>{t('tl_words')}</div></div>
        <div style={toolStyles.wcStat}><div style={toolStyles.wcNum}>{chars}</div><div style={toolStyles.wcLabel}>{t('tl_chars')}</div></div>
        <div style={toolStyles.wcStat}><div style={toolStyles.wcNum}>{charsNoSpace}</div><div style={toolStyles.wcLabel}>{t('tl_nospace')}</div></div>
        <div style={toolStyles.wcStat}><div style={toolStyles.wcNum}>{sentences}</div><div style={toolStyles.wcLabel}>{t('tl_sent')}</div></div>
        <div style={toolStyles.wcStat}><div style={toolStyles.wcNum}>{readTime} min</div><div style={toolStyles.wcLabel}>{t('tl_read')}</div></div>
      </div>
      {text && <button style={toolStyles.convertBtn} onClick={() => setText('')}>{t('tl_clear')}</button>}
    </div>
  );
}

// ── TYPING TUTOR ─────────────────────────────────────────────
const SENTENCES = [
  'The quick brown fox jumps over the lazy dog.',
  'Pi Network is building the first digital currency for everyday people.',
  'PenFlow is a blogging platform powered by Pi cryptocurrency.',
  'Practice makes perfect when it comes to typing speed.',
  'Blockchain technology is revolutionizing the digital economy.',
];

function TypingTutor() {
  const [sentence, setSentence] = useState(SENTENCES[0]);
  const [typed, setTyped] = useState('');
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [wpm, setWpm] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  const inputRef = useRef(null);

  const start = () => {
    const idx = Math.floor(Math.random() * SENTENCES.length);
    setSentence(SENTENCES[idx]);
    setTyped(''); setStarted(false); setFinished(false); setWpm(0); setAccuracy(100);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 100);
  };

  const handleType = (e) => {
    const val = e.target.value;
    if (!started) { setStarted(true); setStartTime(Date.now()); }
    setTyped(val);
    const correct = val.split('').filter((c, i) => c === sentence[i]).length;
    setAccuracy(val.length ? Math.round((correct / val.length) * 100) : 100);
    if (val === sentence) {
      const mins = (Date.now() - startTime) / 60000;
      setWpm(Math.round((sentence.split(' ').length) / mins));
      setFinished(true);
    }
  };

  return (
    <div style={toolStyles.ttWrap}>
      <div style={toolStyles.ttSentence}>
        {sentence.split('').map((ch, i) => (
          <span key={i} style={{
            color: i < typed.length ? (typed[i] === ch ? '#10b981' : '#ff4444') : '#333',
            backgroundColor: i === typed.length ? '#34d399' : 'transparent',
            fontWeight: i === typed.length ? 'bold' : 'normal',
          }}>{ch}</span>
        ))}
      </div>
      <input ref={inputRef} style={toolStyles.ttInput} placeholder={t('tl_typing')} value={typed} onChange={handleType} disabled={finished} />
      <div style={toolStyles.row}>
        <div style={toolStyles.wcStat}><div style={toolStyles.wcNum}>{wpm}</div><div style={toolStyles.wcLabel}>WPM</div></div>
        <div style={toolStyles.wcStat}><div style={toolStyles.wcNum}>{accuracy}%</div><div style={toolStyles.wcLabel}>{t('tl_acc')}</div></div>
        <div style={toolStyles.wcStat}><div style={toolStyles.wcNum}>{typed.length}</div><div style={toolStyles.wcLabel}>{t('tl_typed')}</div></div>
      </div>
      {finished && <div style={toolStyles.result}>{t('tl_done')}<strong>{wpm} WPM</strong> with <strong>{accuracy}% accuracy</strong></div>}
      <button style={toolStyles.convertBtn} onClick={start}>{finished ? '🔄 Try Again' : '🔀 New Sentence'}</button>
    </div>
  );
}

// ── BIRTHDAY CALCULATOR ─────────────────────────────────────────────────────
function BirthdayCalc() {
  const [dob, setDob] = useState('');
  const [result, setResult] = useState(null);

  const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const ZODIAC = [
    {sign:'Capricorn',emoji:'🐐',end:[1,19]},{sign:'Aquarius',emoji:'🏺',end:[2,18]},
    {sign:'Pisces',emoji:'🐟',end:[3,20]},{sign:'Aries',emoji:'🐏',end:[4,19]},
    {sign:'Taurus',emoji:'🐂',end:[5,20]},{sign:'Gemini',emoji:'👯',end:[6,20]},
    {sign:'Cancer',emoji:'🦀',end:[7,22]},{sign:'Leo',emoji:'🦁',end:[8,22]},
    {sign:'Virgo',emoji:'🌾',end:[9,22]},{sign:'Libra',emoji:'⚖️',end:[10,22]},
    {sign:'Scorpio',emoji:'🦂',end:[11,21]},{sign:'Sagittarius',emoji:'🏹',end:[12,21]},
    {sign:'Capricorn',emoji:'🐐',end:[12,31]}
  ];

  const getZodiac = (m, d) => {
    for (let z of ZODIAC) {
      if (m < z.end[0] || (m === z.end[0] && d <= z.end[1])) return z;
    }
    return ZODIAC[0];
  };

  const calculate = () => {
    if (!dob) return;
    const birth = new Date(dob + 'T00:00:00');
    const now = new Date();
    
    let years = now.getFullYear() - birth.getFullYear();
    let months = now.getMonth() - birth.getMonth();
    let days = now.getDate() - birth.getDate();
    
    if (days < 0) { months--; const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0); days += lastMonth.getDate(); }
    if (months < 0) { years--; months += 12; }
    
    const totalDays = Math.floor((now - birth) / (1000*60*60*24));
    const totalWeeks = Math.floor(totalDays / 7);
    const totalHours = totalDays * 24;
    const totalMinutes = totalHours * 60;
    const totalSeconds = totalMinutes * 60;
    const totalMonths = years * 12 + months;
    
    // Next birthday
    const nextBday = new Date(now.getFullYear(), birth.getMonth(), birth.getDate());
    if (nextBday <= now) nextBday.setFullYear(now.getFullYear() + 1);
    const daysToNext = Math.ceil((nextBday - now) / (1000*60*60*24));
    
    const zodiac = getZodiac(birth.getMonth() + 1, birth.getDate());
    
    setResult({
      years, months, days, totalDays, totalWeeks, totalHours, totalMonths, totalMinutes, totalSeconds,
      dayOfBirth: DAYS[birth.getDay()],
      zodiac, daysToNext,
      nextBday: nextBday.toLocaleDateString('en-US', { day:'numeric', month:'long', year:'numeric' }),
      birthDate: `${birth.getDate()} ${MONTHS[birth.getMonth()]} ${birth.getFullYear()}`,
    });
  };

  return (
    <div>
      <div style={{ backgroundColor: '#f3e5f5', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
        <label style={{ color: '#6200ea', fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '8px' }}>{t('tl_dob')}</label>
        <input type="date" value={dob} onChange={e => setDob(e.target.value)}
          style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '2px solid #6200ea', fontSize: '15px', boxSizing: 'border-box' }} />
        <button onClick={calculate} style={{ width: '100%', marginTop: '10px', backgroundColor: '#6200ea', color: 'white', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer' }}>
          🎂 Calculate
        </button>
      </div>

      {result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Main age */}
          <div style={{ backgroundColor: '#6200ea', borderRadius: '16px', padding: '20px', textAlign: 'center', color: 'white' }}>
            <div style={{ fontSize: '13px', opacity: 0.8, marginBottom: '8px' }}>🎂 Born on {result.dayOfBirth}, {result.birthDate}</div>
            <div style={{ fontSize: '36px', fontWeight: '900' }}>{result.years} <span style={{ fontSize: '18px' }}>{t('tl_years')}</span></div>
            <div style={{ fontSize: '18px', marginTop: '4px' }}>{result.months} months · {result.days} days</div>
          </div>

          {/* Zodiac */}
          <div style={{ backgroundColor: '#fff9e6', borderRadius: '12px', padding: '14px', textAlign: 'center', border: '2px solid #34d399' }}>
            <div style={{ fontSize: '32px' }}>{result.zodiac.emoji}</div>
            <div style={{ fontWeight: 'bold', color: '#6200ea', fontSize: '16px' }}>{result.zodiac.sign}</div>
            <div style={{ color: '#999', fontSize: '12px' }}>{t('tl_zsign')}</div>
          </div>

          {/* Stats grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {[
              { label: '📅 Total Days', value: result.totalDays.toLocaleString() },
              { label: '📆 Total Weeks', value: result.totalWeeks.toLocaleString() },
              { label: '🗓️ Total Months', value: result.totalMonths.toLocaleString() },
              { label: '⏰ Total Hours', value: result.totalHours.toLocaleString() },
              { label: '⏱️ Minutes', value: result.totalMinutes.toLocaleString() },
              { label: '⚡ Seconds', value: result.totalSeconds.toLocaleString() },
            ].map((s, i) => (
              <div key={i} style={{ backgroundColor: 'white', borderRadius: '12px', padding: '14px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>{s.label}</div>
                <div style={{ fontWeight: 'bold', color: '#6200ea', fontSize: '18px' }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Next birthday */}
          <div style={{ backgroundColor: '#d1fae5', borderRadius: '12px', padding: '14px', textAlign: 'center', border: '2px solid #10b981' }}>
            <div style={{ fontWeight: 'bold', color: '#065f46', fontSize: '15px' }}>{t('tl_nextb')}</div>
            <div style={{ color: '#065f46', fontSize: '13px', marginTop: '4px' }}>{result.nextBday}</div>
            <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '20px', marginTop: '6px' }}>{result.daysToNext} days to go!</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── BMI CALCULATOR ───────────────────────────────────────────────────────────
function BMICalc() {
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const h = parseFloat(height) / 100;
    const w = parseFloat(weight);
    if (!h || !w) return;
    const bmi = (w / (h * h)).toFixed(1);
    let category, color, emoji;
    if (bmi < 18.5) { category = 'Underweight'; color = '#3b82f6'; emoji = '😟'; }
    else if (bmi < 25) { category = 'Normal Weight'; color = '#10b981'; emoji = '😊'; }
    else if (bmi < 30) { category = 'Overweight'; color = '#0d9488'; emoji = '😐'; }
    else { category = 'Obese'; color = '#ef4444'; emoji = '😟'; }
    setResult({ bmi, category, color, emoji });
  };

  return (
    <div>
      <div style={{ backgroundColor: '#f3e5f5', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
        <label style={{ color: '#6200ea', fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px' }}>{t('tl_height')}</label>
        <input type="number" value={height} onChange={e => setHeight(e.target.value)} placeholder="e.g. 175"
          style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px solid #6200ea', fontSize: '15px', boxSizing: 'border-box', marginBottom: '12px' }} />
        <label style={{ color: '#6200ea', fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px' }}>{t('tl_weight')}</label>
        <input type="number" value={weight} onChange={e => setWeight(e.target.value)} placeholder="e.g. 70"
          style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px solid #6200ea', fontSize: '15px', boxSizing: 'border-box', marginBottom: '12px' }} />
        <button onClick={calculate} style={{ width: '100%', backgroundColor: '#6200ea', color: 'white', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer' }}>
          ⚖️ Calculate BMI
        </button>
      </div>
      {result && (
        <div style={{ backgroundColor: result.color + '22', border: `2px solid ${result.color}`, borderRadius: '16px', padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px' }}>{result.emoji}</div>
          <div style={{ fontSize: '48px', fontWeight: '900', color: result.color }}>{result.bmi}</div>
          <div style={{ fontWeight: 'bold', color: result.color, fontSize: '18px' }}>{result.category}</div>
          <div style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>BMI = weight(kg) / height²(m)</div>
          <div style={{ marginTop: '12px', backgroundColor: 'white', borderRadius: '10px', padding: '10px', fontSize: '12px', color: '#666' }}>
            <div>Underweight: &lt;18.5 | Normal: 18.5-24.9</div>
            <div>Overweight: 25-29.9 | Obese: &gt;30</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CURRENCY CALCULATOR ────────────────────────────────────────────────────────
function CurrencyCalc() {
  const [amount, setAmount] = useState('1');
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('PKR');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const CURRENCIES = ['USD','EUR','GBP','PKR','INR','SAR','AED','CNY','JPY','CAD','AUD','CHF','TRY','RUB','BRL'];

  const convert = async () => {
    setLoading(true);
    try {
      const res = await fetch(`https://api.exchangerate-api.com/v4/latest/${from}`);
      const data = await res.json();
      const rate = data.rates[to];
      setResult({ value: (parseFloat(amount) * rate).toFixed(2), rate: rate.toFixed(4) });
    } catch(e) {
      setResult({ value: 'Error', rate: 'API Error' });
    }
    setLoading(false);
  };

  return (
    <div>
      <div style={{ backgroundColor: '#f3e5f5', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
        <label style={{ color: '#6200ea', fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px' }}>{t('tl_amount')}</label>
        <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
          style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px solid #6200ea', fontSize: '15px', boxSizing: 'border-box', marginBottom: '12px' }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
          <select value={from} onChange={e => setFrom(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '2px solid #6200ea', fontSize: '14px' }}>
            {CURRENCIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <span style={{ textAlign: 'center', fontWeight: 'bold', color: '#6200ea' }}>→</span>
          <select value={to} onChange={e => setTo(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '2px solid #6200ea', fontSize: '14px' }}>
            {CURRENCIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <button onClick={convert} disabled={loading} style={{ width: '100%', backgroundColor: '#6200ea', color: 'white', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer' }}>
          {loading ? '⏳ Converting...' : '💱 Convert'}
        </button>
      </div>
      {result && (
        <div style={{ backgroundColor: '#d1fae5', border: '2px solid #10b981', borderRadius: '16px', padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '13px', color: '#065f46' }}>{amount} {from} =</div>
          <div style={{ fontSize: '36px', fontWeight: '900', color: '#065f46' }}>{result.value} {to}</div>
          <div style={{ fontSize: '12px', color: '#10b981', marginTop: '6px' }}>Rate: 1 {from} = {result.rate} {to}</div>
        </div>
      )}
    </div>
  );
}

// ── COLOR PICKER ──────────────────────────────────────────────────────────────
function ColorPicker() {
  const [color, setColor] = useState('#6200ea');

  const hexToRgb = (hex) => {
    const r = parseInt(hex.slice(1,3),16);
    const g = parseInt(hex.slice(3,5),16);
    const b = parseInt(hex.slice(5,7),16);
    return {r,g,b};
  };

  const hexToHsl = (hex) => {
    let {r,g,b} = hexToRgb(hex);
    r/=255; g/=255; b/=255;
    const max=Math.max(r,g,b), min=Math.min(r,g,b);
    let h,s,l=(max+min)/2;
    if(max===min){h=s=0;}
    else{const d=max-min;s=l>0.5?d/(2-max-min):d/(max+min);
    switch(max){case r:h=(g-b)/d+(g<b?6:0);break;case g:h=(b-r)/d+2;break;case b:h=(r-g)/d+4;break;}
    h/=6;}
    return {h:Math.round(h*360),s:Math.round(s*100),l:Math.round(l*100)};
  };

  const rgb = hexToRgb(color);
  const hsl = hexToHsl(color);

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <div style={{ width: '100%', height: '120px', backgroundColor: color, borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} />
        <input type="color" value={color} onChange={e => setColor(e.target.value)}
          style={{ width: '80px', height: '80px', border: 'none', borderRadius: '50%', cursor: 'pointer', padding: 0 }} />
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[
            { label: 'HEX', value: color.toUpperCase() },
            { label: 'RGB', value: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` },
            { label: 'HSL', value: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` },
          ].map(item => (
            <div key={item.label} onClick={() => navigator.clipboard?.writeText(item.value)}
              style={{ backgroundColor: 'white', borderRadius: '10px', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', boxShadow: '0 2px 6px rgba(0,0,0,0.08)', cursor: 'pointer' }}>
              <span style={{ color: '#999', fontWeight: 'bold', fontSize: '13px' }}>{item.label}</span>
              <span style={{ color: '#333', fontWeight: 'bold', fontSize: '13px' }}>{item.value}</span>
            </div>
          ))}
        </div>
        <p style={{ color: '#999', fontSize: '12px' }}>{t('tl_tapcopy')}</p>
      </div>
    </div>
  );
}

// ── QR GENERATOR ──────────────────────────────────────────────────────────────
function QRGen() {
  const [text, setText] = useState(window.location.origin);
  const [qrUrl, setQrUrl] = useState('');

  const generate = () => {
    setQrUrl(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(text)}`);
  };

  return (
    <div>
      <div style={{ backgroundColor: '#f3e5f5', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
        <label style={{ color: '#6200ea', fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px' }}>{t('tl_url')}</label>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
          style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px solid #6200ea', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: '12px' }} />
        <button onClick={generate} style={{ width: '100%', backgroundColor: '#6200ea', color: 'white', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer' }}>
          📱 Generate QR Code
        </button>
      </div>
      {qrUrl && (
        <div style={{ textAlign: 'center', backgroundColor: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <img src={qrUrl} alt={t('tl_qr')} style={{ width: '200px', height: '200px' }} />
          <p style={{ color: '#999', fontSize: '12px', marginTop: '10px' }}>{t('tl_scan')}</p>
          <a href={qrUrl} download="qrcode.png" style={{ color: '#6200ea', fontWeight: 'bold', fontSize: '13px' }}>{t('tl_dlqr')}</a>
        </div>
      )}
    </div>
  );
}

// ── MAIN TOOLS PAGE ──────────────────────────────────────────
function Tools() {
  const [activeTool, setActiveTool] = useState('calculator');

  const tools = [
    { id: 'calculator', label: '🧮 Scientific Calc' },
    { id: 'converter', label: '📐 Unit Converter' },
    { id: 'wordcount', label: '📝 Word Counter' },
    { id: 'typing', label: '⌨️ Typing Tutor' },
    { id: 'bday', label: '🎂 Birthday Calc' },
    { id: 'bmi', label: '⚖️ BMI Calculator' },
    { id: 'currency', label: '💱 Currency Calc' },
    { id: 'color', label: '🎨 Color Picker' },
    { id: 'qr', label: '📱 QR Generator' },
  ];

  return (
    <div style={toolStyles.page}>
      <h1 style={toolStyles.title}>{t('tl_title')}</h1>
      <p style={toolStyles.sub}>{t('tl_tagline')}</p>
      <div style={toolStyles.tabs}>
        {tools.map(t => (
          <button key={t.id}
            style={{ ...toolStyles.tab, ...(activeTool === t.id ? toolStyles.tabActive : {}) }}
            onClick={() => setActiveTool(t.id)}
          >{t.label}</button>
        ))}
      </div>
      <div style={toolStyles.toolBox}>
        {activeTool === 'calculator' && <Calculator />}
        {activeTool === 'converter' && <UnitConverter />}
        {activeTool === 'wordcount' && <WordCounter />}
        {activeTool === 'typing' && <TypingTutor />}
        {activeTool === 'bday' && <BirthdayCalc />}
        {activeTool === 'bmi' && <BMICalc />}
        {activeTool === 'currency' && <CurrencyCalc />}
        {activeTool === 'color' && <ColorPicker />}
        {activeTool === 'qr' && <QRGen />}
      </div>
    </div>
  );
}

const toolStyles = {
  page: { maxWidth: '600px', margin: '20px auto', padding: '20px' },
  title: { textAlign: 'center', color: '#6200ea', marginBottom: '5px' },
  sub: { textAlign: 'center', color: '#999', marginBottom: '20px', fontSize: '14px' },
  tabs: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px', justifyContent: 'center' },
  tab: { padding: '8px 16px', borderRadius: '20px', border: '2px solid #6200ea', backgroundColor: 'white', color: '#6200ea', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
  tabActive: { backgroundColor: '#6200ea', color: 'white' },
  toolBox: { backgroundColor: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
  // Calculator
  calcWrap: { maxWidth: '300px', margin: '0 auto' },
  calcDisplay: { backgroundColor: '#1a1a2e', color: 'white', padding: '15px 20px', borderRadius: '12px', fontSize: '28px', textAlign: 'right', marginBottom: '10px', minHeight: '60px', wordBreak: 'break-all' },
  calcGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' },
  calcBtn: { padding: '18px', borderRadius: '10px', border: 'none', fontSize: '18px', cursor: 'pointer', fontWeight: 'bold' },
  // Converter
  converterWrap: { display: 'flex', flexDirection: 'column', gap: '12px' },
  row: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  catBtn: { padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
  input: { padding: '10px', borderRadius: '10px', border: '2px solid #e0e0e0', fontSize: '16px', width: '100%', boxSizing: 'border-box' },
  select: { padding: '8px', borderRadius: '10px', border: '2px solid #e0e0e0', fontSize: '14px', flex: 1 },
  convertBtn: { backgroundColor: '#6200ea', color: 'white', border: 'none', padding: '12px', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', width: '100%' },
  result: { backgroundColor: '#f3e5f5', padding: '12px', borderRadius: '10px', color: '#6200ea', fontSize: '15px', textAlign: 'center' },
  // Word Counter
  wcWrap: { display: 'flex', flexDirection: 'column', gap: '12px' },
  wcTextarea: { padding: '12px', borderRadius: '10px', border: '2px solid #e0e0e0', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit' },
  wcStats: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' },
  wcStat: { backgroundColor: '#f3e5f5', borderRadius: '10px', padding: '10px', textAlign: 'center' },
  wcNum: { fontSize: '20px', fontWeight: 'bold', color: '#6200ea' },
  wcLabel: { fontSize: '11px', color: '#666', marginTop: '2px' },
  // Typing Tutor
  ttWrap: { display: 'flex', flexDirection: 'column', gap: '12px' },
  ttSentence: { backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '10px', fontSize: '18px', lineHeight: '1.8', letterSpacing: '1px', fontFamily: 'monospace' },
  ttInput: { padding: '12px', borderRadius: '10px', border: '2px solid #6200ea', fontSize: '16px', fontFamily: 'monospace' },
};

export default Tools;
